/**
 * WebhookSettings — /w/:slug/app/settings/webhooks
 *
 * PR-Cluster-7-b (docs/ux-design-plan.md §10) — Marcus persona's
 * pull-integration story finally has a UI. Backend routes shipped
 * in Phase D per docs/uc-2-provenance-api-spec.md §7.5.
 *
 * Scope of this first slice:
 *   - List active + revoked/paused endpoints
 *   - Create endpoint (returns plaintext secret ONCE with copy affordance)
 *   - Rotate secret (overlap 0 / 1h / 24h / 7d / 30d picker; new
 *     plaintext also ONE-TIME reveal)
 *   - Pause / Resume / Disable per-endpoint
 *   - Recent deliveries drawer (last 100 attempts)
 *
 * PR-Cluster-7-c polish:
 *   - Signature-verify code snippets (Node.js + Python + curl-probe)
 *   - Egress CIDR banner linking to /api/webhooks/egress-ranges.json
 *
 * Deferred:
 *   - Test-event with synthetic-payload preview per P-UC7-4
 *   - Bulk-replay + keyboard j/k/Enter/R/C in delivery log (P-UC7-8)
 */

import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Badge, Button, EmptyState, Field, Input, PageHeader, Select, Spinner } from '@aigovops/design-system';
import { useOptionalWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import {
  createWebhookEndpoint,
  listWebhookDeliveries,
  listWebhookEndpoints,
  replayWebhookDelivery,
  rotateWebhookSecret,
  sendWebhookTestEvent,
  setWebhookStatus,
  type CreatedWebhookResponse,
  type WebhookDeliveryResult,
  type WebhookDeliveryRow,
  type WebhookEndpointSummary,
  type WebhookOverlapHours,
  type WebhookStatus,
} from '../lib/api';
import { fmtDate } from '../lib/format';

export function WebhookSettings() {
  const workspaceCtx = useOptionalWorkspace();
  const slug = workspaceCtx?.workspace.slug ?? null;
  const canManage =
    workspaceCtx?.role === 'owner' || workspaceCtx?.role === 'admin';

  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createdRevealed, setCreatedRevealed] = useState<CreatedWebhookResponse | null>(null);
  const [rotatedRevealed, setRotatedRevealed] = useState<
    { endpointId: string; plaintext: string; overlapUntil: string | null } | null
  >(null);

  const endpoints = useWorkspaceQuery(
    (slug) => queryKeys.webhookEndpoints(slug),
    (slug) => listWebhookEndpoints(slug),
  );

  if (!slug) return null;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)', maxWidth: '68rem' }}>
      <PageHeader
        title="Audit log webhooks"
        subtitle="Push every audit event to your GRC or observability stack."
        right={
          canManage ? (
            <Button variant="primary" compact onClick={() => setShowCreate((s) => !s)}>
              {showCreate ? 'Cancel' : '+ Add endpoint'}
            </Button>
          ) : null
        }
      />

      {/* Panel H8: cross-link to the notification webhooks (findings + pack validity). */}
      <p style={{ margin: 0, fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
        Looking for finding and pack-validity notifications? Those are{' '}
        <RouterLink to={`/w/${slug}/app/settings/event-webhooks`} style={{ color: 'var(--accent)' }}>Notification webhooks</RouterLink>.
      </p>

      {error ? <Alert variant="error">{error}</Alert> : null}
      {!canManage ? (
        <Alert variant="info">
          Only workspace owners and admins can create or manage webhook endpoints. You can view existing endpoints below.
        </Alert>
      ) : null}

      {createdRevealed ? (
        <SecretRevealBanner
          title="Endpoint created — copy the secret now"
          detail="This plaintext will never be shown again. Store it in your subscriber's secret manager, then rotate here if it leaks."
          plaintext={createdRevealed.secret_plaintext}
          onDismiss={() => setCreatedRevealed(null)}
        />
      ) : null}
      {rotatedRevealed ? (
        <SecretRevealBanner
          title="Secret rotated — copy the new secret now"
          detail={
            rotatedRevealed.overlapUntil
              ? `The old secret keeps verifying until ${fmtDate(rotatedRevealed.overlapUntil)} (overlap window). After that, only this new secret validates signatures.`
              : 'No overlap window — the old secret stops validating immediately.'
          }
          plaintext={rotatedRevealed.plaintext}
          onDismiss={() => setRotatedRevealed(null)}
        />
      ) : null}

      {showCreate && canManage ? (
        <CreateEndpointForm
          slug={slug}
          onCreated={(created) => {
            setCreatedRevealed(created);
            setShowCreate(false);
            endpoints.refetch();
          }}
          onError={(msg) => setError(msg)}
          onCancel={() => setShowCreate(false)}
        />
      ) : null}

      {endpoints.loading ? (
        <Spinner label="Loading endpoints" />
      ) : endpoints.error ? (
        <Alert variant="error">{endpoints.error}</Alert>
      ) : !endpoints.data || endpoints.data.endpoints.length === 0 ? (
        <EmptyState
          title="No webhook endpoints"
          description="Add a subscriber URL to receive every audit event as it happens."
          action={
            canManage ? (
              <Button variant="primary" onClick={() => setShowCreate(true)}>
                + Add endpoint
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          {endpoints.data.endpoints.map((e) => (
            <EndpointCard
              key={e.endpoint_id}
              slug={slug}
              endpoint={e}
              canManage={canManage}
              onRotated={(plaintext, overlapUntil) => {
                setRotatedRevealed({ endpointId: e.endpoint_id, plaintext, overlapUntil });
                endpoints.refetch();
              }}
              onStatusChanged={() => endpoints.refetch()}
              onError={setError}
            />
          ))}
        </div>
      )}

      {/* PR-Cluster-7-c polish: infrastructure & integration guidance
          that used to live in undocumented punch-list territory.
          Ships as collapsed disclosures so scan-mode readers focus on
          their endpoint list and open the reference material on
          demand. */}
      <EgressBanner />
      <SignatureVerifySnippets />
    </div>
  );
}

// ---- Egress CIDR banner ----------------------------------------------

function EgressBanner() {
  return (
    <details
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
      }}
    >
      <summary
        style={{
          cursor: 'pointer',
          padding: 'var(--space-3) var(--space-4)',
          fontSize: 'var(--font-size-2xs)',
          color: 'var(--text)',
          fontWeight: 500,
          listStyle: 'none',
        }}
      >
        <span aria-hidden style={{ marginRight: 'var(--space-2)', color: 'var(--text-dim)' }}>
          ▸
        </span>
        Add gacpro egress IPs to your subscriber's allowlist
      </summary>
      <div style={{ padding: '0 var(--space-4) var(--space-4)', display: 'grid', gap: 'var(--space-2)', fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
        <p style={{ margin: 0 }}>
          Webhook deliveries originate from Cloudflare's public IPv4 + IPv6 ranges. The full
          machine-readable list ships as JSON at{' '}
          <a
            href="/api/webhooks/egress-ranges.json"
            target="_blank"
            rel="noreferrer"
            style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}
          >
            /api/webhooks/egress-ranges.json
          </a>
          {' '}(publicly readable; versioned so you can watch for changes).
        </p>
        <p style={{ margin: 0 }}>
          Pin against the version field, refresh weekly. Ranges change infrequently but announcements
          land on a public RSS feed the operator can subscribe to.
        </p>
      </div>
    </details>
  );
}

// ---- Signature-verify code snippets ----------------------------------

const SIGN_HEADER = 'X-gacpro-Signature: t=<unix-ts>,v1=<hex>';

const SNIPPETS: Array<{ label: string; body: string }> = [
  {
    label: 'Node.js',
    body: `import crypto from 'node:crypto';

// Every request carries: X-gacpro-Signature: t=<unix-ts>,v1=<hex>
// v1 = HMAC-SHA256(secret, ts + '.' + rawBody)
// 5-minute replay tolerance is enforced by the gacpro sender; you
// should re-check on the receiver's clock.

export function verifyGacproSignature(
  rawBody: string,
  header: string,
  secret: string,
): boolean {
  const parts = Object.fromEntries(
    header.split(',').map((p) => p.split('=').map((s) => s.trim())),
  );
  if (!parts.t || !parts.v1) return false;
  const skewSeconds = Math.abs(Date.now() / 1000 - Number(parts.t));
  if (skewSeconds > 300) return false;
  const expected = crypto
    .createHmac('sha256', secret)
    .update(parts.t + '.' + rawBody)
    .digest('hex');
  // Constant-time compare — never use === on the hex strings.
  return crypto.timingSafeEqual(
    Buffer.from(expected, 'hex'),
    Buffer.from(parts.v1, 'hex'),
  );
}`,
  },
  {
    label: 'Python',
    body: `import hmac, hashlib, time

def verify_gacpro_signature(raw_body: bytes, header: str, secret: str) -> bool:
    parts = dict(kv.split('=', 1) for kv in header.split(','))
    ts, sig = parts.get('t'), parts.get('v1')
    if not ts or not sig:
        return False
    if abs(time.time() - int(ts)) > 300:
        return False
    expected = hmac.new(
        secret.encode('utf-8'),
        f"{ts}.".encode('utf-8') + raw_body,
        hashlib.sha256,
    ).hexdigest()
    # hmac.compare_digest is constant-time.
    return hmac.compare_digest(expected, sig)`,
  },
  {
    label: 'curl (probe)',
    body: `# Given a captured payload + header, verify locally with openssl
# so you can debug signature mismatches without running your app.
TS=1735689600  # from X-gacpro-Signature "t=" segment
SIG=abc123...  # from X-gacpro-Signature "v1=" segment
SECRET=whs_...      # your endpoint's plaintext secret

printf "%s.%s" "$TS" "$(cat request.body)" \\
  | openssl dgst -sha256 -hmac "$SECRET" -hex \\
  | awk '{print $2}' \\
  | grep -qx "$SIG" && echo '✓ signature valid' || echo '✗ mismatch'`,
  },
];

function SignatureVerifySnippets() {
  return (
    <details
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
      }}
    >
      <summary
        style={{
          cursor: 'pointer',
          padding: 'var(--space-3) var(--space-4)',
          fontSize: 'var(--font-size-2xs)',
          color: 'var(--text)',
          fontWeight: 500,
          listStyle: 'none',
        }}
      >
        <span aria-hidden style={{ marginRight: 'var(--space-2)', color: 'var(--text-dim)' }}>
          ▸
        </span>
        Verify webhook signatures ({SNIPPETS.length} language examples)
      </summary>
      <div style={{ padding: '0 var(--space-4) var(--space-4)', display: 'grid', gap: 'var(--space-3)' }}>
        <p style={{ margin: 0, fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
          Every request carries an{' '}
          <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>
            {SIGN_HEADER}
          </code>
          . <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>v1</code> is{' '}
          <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>
            HMAC-SHA256(secret, ts + '.' + raw_body)
          </code>
          . Verify on RAW bytes (not a re-serialised JSON string) and use constant-time comparison.
        </p>
        {SNIPPETS.map((s) => (
          <CodeBlock key={s.label} label={s.label} body={s.body} />
        ))}
      </div>
    </details>
  );
}

function CodeBlock({ label, body }: { label: string; body: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignored
    }
  };
  return (
    <figure style={{ margin: 0 }}>
      <figcaption
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '4px var(--space-2)',
          background: 'var(--surface-alt)',
          border: '1px solid var(--border)',
          borderBottom: 0,
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
        }}
      >
        <span>{label}</span>
        <button
          type="button"
          onClick={copy}
          style={{
            appearance: 'none',
            background: 'transparent',
            border: 0,
            color: 'var(--accent)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-3xs)',
            padding: 0,
          }}
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </figcaption>
      <pre
        style={{
          margin: 0,
          padding: 'var(--space-3)',
          background: 'var(--surface-alt)',
          border: '1px solid var(--border)',
          borderBottomLeftRadius: '4px',
          borderBottomRightRadius: '4px',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text)',
          overflow: 'auto',
          maxHeight: '22rem',
        }}
      >
        {body}
      </pre>
    </figure>
  );
}

// ---- Secret reveal banner (one-time) ---------------------------------

function SecretRevealBanner(props: {
  title: string;
  detail: string;
  plaintext: string;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(props.plaintext);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API disabled in some contexts — user can still
      // triple-click the field. No fallback modal needed.
    }
  };
  return (
    <section
      aria-label={props.title}
      style={{
        border: '1px solid var(--band-amber)',
        borderLeftWidth: '4px',
        borderRadius: 'var(--radius-card)',
        background: 'var(--warning-bg)',
        padding: 'var(--space-4) var(--space-5)',
        color: 'var(--warning-fg)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--space-3)' }}>
        <strong style={{ fontSize: 'var(--font-size-md)', fontWeight: 700 }}>{props.title}</strong>
        <button
          type="button"
          onClick={props.onDismiss}
          aria-label="Dismiss"
          style={{
            appearance: 'none',
            background: 'transparent',
            border: 0,
            color: 'var(--warning-fg)',
            fontSize: 'var(--font-size-md)',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          ×
        </button>
      </div>
      <p style={{ margin: 0, fontSize: 'var(--font-size-2xs)' }}>{props.detail}</p>
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'stretch' }}>
        <code
          style={{
            flex: 1,
            display: 'block',
            padding: 'var(--space-2) var(--space-3)',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '4px',
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--font-size-2xs)',
            color: 'var(--text)',
            overflow: 'auto',
            wordBreak: 'break-all',
          }}
        >
          {props.plaintext}
        </code>
        <Button variant="secondary" compact onClick={copy}>
          {copied ? '✓ Copied' : 'Copy'}
        </Button>
      </div>
    </section>
  );
}

// ---- Create form -----------------------------------------------------

function CreateEndpointForm(props: {
  slug: string;
  onCreated: (created: CreatedWebhookResponse) => void;
  onError: (msg: string) => void;
  onCancel: () => void;
}) {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const created = await createWebhookEndpoint(props.slug, {
        url: url.trim(),
        label: label.trim(),
      });
      props.onCreated(created);
      setUrl('');
      setLabel('');
    } catch (err) {
      props.onError(err instanceof Error ? err.message : 'Failed to create endpoint.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      style={{
        display: 'grid',
        gap: 'var(--space-3)',
        padding: 'var(--space-4) var(--space-5)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)' }}>Add webhook endpoint</h2>
      <Field id="webhook-url" label="Endpoint URL">
        <Input
          id="webhook-url"
          type="url"
          required
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://your-grc.example.com/hooks/fedsec"
          autoComplete="off"
        />
      </Field>
      <Field id="webhook-label" label="Label">
        <Input
          id="webhook-label"
          type="text"
          required
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Datadog Prod"
          autoComplete="off"
        />
      </Field>
      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
        <Button variant="secondary" compact onClick={props.onCancel} type="button">
          Cancel
        </Button>
        <Button variant="primary" compact type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create endpoint'}
        </Button>
      </div>
    </form>
  );
}

// ---- Endpoint card ---------------------------------------------------

function EndpointCard(props: {
  slug: string;
  endpoint: WebhookEndpointSummary;
  canManage: boolean;
  onRotated: (plaintext: string, overlapUntil: string | null) => void;
  onStatusChanged: () => void;
  onError: (msg: string) => void;
}) {
  const { endpoint: e } = props;
  const [showDeliveries, setShowDeliveries] = useState(false);
  const [rotateOverlap, setRotateOverlap] = useState<WebhookOverlapHours>(24);
  const [rotating, setRotating] = useState(false);
  const [busyStatus, setBusyStatus] = useState<null | WebhookStatus>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<
    { deliveryId: string; at: string } | null
  >(null);

  const rotate = async () => {
    setRotating(true);
    try {
      const r = await rotateWebhookSecret(props.slug, e.endpoint_id, rotateOverlap);
      props.onRotated(r.secret_plaintext, r.overlap_until);
    } catch (err) {
      props.onError(err instanceof Error ? err.message : 'Rotation failed.');
    } finally {
      setRotating(false);
    }
  };

  // PR-Cluster-7-e: fire a synthetic webhook.test event at this
  // endpoint. Server emits the real audit event, signs the payload
  // with the endpoint's HMAC secret, and dispatches once — bypasses
  // the endpoint's event_filter so subscribers can prove their
  // integration works before opting into any real event type.
  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await sendWebhookTestEvent(props.slug, e.endpoint_id);
      setTestResult({ deliveryId: r.delivery_id, at: new Date().toISOString() });
      // Refresh the endpoint list so last_delivery_at ticks over.
      props.onStatusChanged();
    } catch (err) {
      props.onError(err instanceof Error ? err.message : 'Test dispatch failed.');
    } finally {
      setTesting(false);
    }
  };

  const changeStatus = async (op: 'pause' | 'resume' | 'disable') => {
    const nextStatus: WebhookStatus =
      op === 'pause' ? 'paused' : op === 'resume' ? 'active' : 'disabled';
    setBusyStatus(nextStatus);
    try {
      await setWebhookStatus(props.slug, e.endpoint_id, op);
      props.onStatusChanged();
    } catch (err) {
      props.onError(err instanceof Error ? err.message : 'Status change failed.');
    } finally {
      setBusyStatus(null);
    }
  };

  return (
    <section
      aria-label={e.label}
      style={{
        border: '1px solid var(--border)',
        borderLeftWidth: '3px',
        borderLeftColor: statusBorder(e.status),
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4) var(--space-5)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <div style={{ display: 'grid', gap: '4px' }}>
          <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600 }}>{e.label}</h3>
          <code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)', wordBreak: 'break-all' }}>
            {e.url}
          </code>
        </div>
        <StatusPill status={e.status} />
      </header>

      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))',
          gap: 'var(--space-2) var(--space-4)',
          margin: 0,
          fontSize: 'var(--font-size-3xs)',
        }}
      >
        <MetaRow label="Prefix">
          <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>{e.prefix}…</code>
        </MetaRow>
        <MetaRow label="Binding">{e.binding_type}</MetaRow>
        <MetaRow label="Filter">
          {e.event_filter.length > 0
            ? `${e.event_filter.length} type${e.event_filter.length === 1 ? '' : 's'}`
            : 'all events'}
        </MetaRow>
        <MetaRow label="Last delivery">
          {e.last_delivery_at ? (
            <>
              {fmtDate(e.last_delivery_at)} · <LastDeliveryTag result={e.last_delivery_status} />
            </>
          ) : (
            <span style={{ color: 'var(--text-dim)' }}>—</span>
          )}
        </MetaRow>
      </dl>

      {props.canManage ? (
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap', paddingTop: 'var(--space-2)', borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <label style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }} htmlFor={`rotate-${e.endpoint_id}`}>
              Rotate with overlap
            </label>
            <Select
              id={`rotate-${e.endpoint_id}`}
              value={String(rotateOverlap)}
              onChange={(ev) => setRotateOverlap(Number(ev.target.value) as WebhookOverlapHours)}
            >
              <option value="0">none</option>
              <option value="1">1 hour</option>
              <option value="24">24 hours</option>
              <option value="168">7 days</option>
              <option value="720">30 days</option>
            </Select>
            <Button variant="secondary" compact onClick={rotate} disabled={rotating}>
              {rotating ? 'Rotating…' : 'Rotate secret'}
            </Button>
          </div>
          <div style={{ flex: 1 }} />
          {e.status === 'active' ? (
            <Button
              variant="secondary"
              compact
              onClick={() => void sendTest()}
              disabled={testing}
              title="Send a synthetic webhook.test event to this endpoint. Bypasses the endpoint's event_filter."
            >
              {testing ? 'Sending…' : 'Send test event'}
            </Button>
          ) : null}
          {e.status !== 'disabled' ? (
            e.status === 'paused' ? (
              <Button variant="secondary" compact onClick={() => void changeStatus('resume')} disabled={busyStatus !== null}>
                Resume
              </Button>
            ) : (
              <Button variant="ghost" compact onClick={() => void changeStatus('pause')} disabled={busyStatus !== null}>
                Pause
              </Button>
            )
          ) : null}
          {e.status !== 'disabled' ? (
            <Button variant="ghost" compact onClick={() => void changeStatus('disable')} disabled={busyStatus !== null}>
              Disable
            </Button>
          ) : null}
        </div>
      ) : null}

      {testResult ? (
        <div
          role="status"
          style={{
            padding: 'var(--space-2) var(--space-3)',
            background: 'var(--surface-alt)',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-card)',
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--text)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            flexWrap: 'wrap',
          }}
        >
          <strong style={{ color: 'var(--accent)' }}>Test dispatched</strong>
          <span style={{ color: 'var(--text-muted)' }}>
            <time dateTime={testResult.at}>{fmtDate(testResult.at)}</time> · delivery{' '}
            <code style={{ fontFamily: 'var(--font-mono)' }}>
              {testResult.deliveryId.slice(0, 12)}…
            </code>
          </span>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            onClick={() => setShowDeliveries(true)}
            style={{
              appearance: 'none',
              background: 'transparent',
              border: 0,
              color: 'var(--accent)',
              fontSize: 'var(--font-size-3xs)',
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline',
            }}
          >
            View in deliveries →
          </button>
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => setShowDeliveries((s) => !s)}
        aria-expanded={showDeliveries}
        style={{
          appearance: 'none',
          background: 'transparent',
          border: 0,
          color: 'var(--accent)',
          fontSize: 'var(--font-size-3xs)',
          cursor: 'pointer',
          padding: 0,
          justifySelf: 'start',
          textDecoration: 'underline',
        }}
      >
        {showDeliveries ? 'Hide deliveries ▾' : 'Show recent deliveries ▸'}
      </button>
      {showDeliveries ? <DeliveryLog slug={props.slug} endpointId={e.endpoint_id} /> : null}
    </section>
  );
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 0 }}>
      <dt
        style={{
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text-dim)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
          margin: 0,
        }}
      >
        {label}
      </dt>
      <dd style={{ margin: 0, color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>{children}</dd>
    </div>
  );
}

function StatusPill({ status }: { status: WebhookStatus }) {
  const tone: Record<WebhookStatus, { bg: string; fg: string; label: string }> = {
    active: { bg: 'var(--success-bg)', fg: 'var(--success-fg)', label: 'Active' },
    paused: { bg: 'var(--warning-bg)', fg: 'var(--warning-fg)', label: 'Paused' },
    disabled: { bg: 'var(--surface-alt)', fg: 'var(--text-muted)', label: 'Disabled' },
  };
  const t = tone[status];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        background: t.bg,
        color: t.fg,
      }}
    >
      {t.label}
    </span>
  );
}

function LastDeliveryTag({ result }: { result: WebhookEndpointSummary['last_delivery_status'] }) {
  if (!result) return null;
  const color: Record<NonNullable<typeof result>, string> = {
    pending: 'var(--text-muted)',
    success: 'var(--success-fg)',
    failure_4xx: 'var(--danger-fg)',
    failure_5xx: 'var(--danger-fg)',
    failure_timeout: 'var(--warning-fg)',
    failure_dns: 'var(--warning-fg)',
  };
  return (
    <span style={{ color: color[result], fontWeight: 600, fontSize: 'var(--font-size-3xs)' }}>
      {result.replace('_', ' ')}
    </span>
  );
}

function statusBorder(status: WebhookStatus): string {
  switch (status) {
    case 'active':
      return 'var(--band-green)';
    case 'paused':
      return 'var(--band-amber)';
    case 'disabled':
      return 'var(--border)';
  }
}

// ---- Delivery log ----------------------------------------------------

/**
 * DeliveryLog — recent-deliveries drawer for one webhook endpoint.
 *
 * PR-Cluster-7-f (partial P-UC7-8): keyboard navigation. Focused
 * row is highlighted; j/k / ArrowDown/ArrowUp cycle focus; Enter
 * expands the row into an in-place details panel with all fields
 * the SPA hides for brevity; C copies the row's idempotency key to
 * the clipboard so an operator can grep for it in their server
 * logs. R (replay) waits on a server-side replay endpoint that
 * isn't shipped yet.
 *
 * PR-Cluster-7-i (P-UC7-8 bulk): checkbox column + "Select
 * failures" quick action + a bulk-replay button above the table.
 * Selection lives alongside j/k focus and doesn't interfere; the
 * bulk replay walks the selection SERIALLY so we don't fan out N
 * concurrent POSTs at the customer's endpoint, and each failure is
 * reported per-delivery without aborting the rest. Same
 * webhook.replayed audit event fires per delivery on the backend —
 * bulk is a UX affordance, not a new endpoint.
 *
 * Focus lives in local state, not the DOM, so it survives virtual-
 * scroll re-mounts if this list grows big enough for windowing.
 */
function DeliveryLog({ slug, endpointId }: { slug: string; endpointId: string }) {
  const [rows, setRows] = useState<WebhookDeliveryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [focusIndex, setFocusIndex] = useState<number>(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [replayingId, setReplayingId] = useState<string | null>(null);
  const [replayedFlash, setReplayedFlash] = useState<{
    priorId: string;
    newDeliveryId: string;
  } | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkState, setBulkState] = useState<{
    total: number;
    done: number;
    ok: number;
    failures: Array<{ deliveryId: string; reason: string }>;
  } | null>(null);

  const doReplay = useCallback(
    async (row: WebhookDeliveryRow) => {
      if (replayingId) return;
      setReplayingId(row.delivery_id);
      try {
        const r = await replayWebhookDelivery(slug, row.delivery_id);
        setReplayedFlash({ priorId: row.delivery_id, newDeliveryId: r.delivery_id });
        setTimeout(
          () =>
            setReplayedFlash((cur) => (cur?.priorId === row.delivery_id ? null : cur)),
          2400,
        );
        // Refetch so the new delivery row shows up in-place.
        const res = await listWebhookDeliveries(slug, endpointId, 100);
        setRows(res.deliveries);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Replay failed.');
      } finally {
        setReplayingId(null);
      }
    },
    [slug, endpointId, replayingId],
  );

  const toggleSelected = useCallback((deliveryId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(deliveryId)) next.delete(deliveryId);
      else next.add(deliveryId);
      return next;
    });
  }, []);

  const selectAllFailures = useCallback(() => {
    if (!rows) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const r of rows) {
        if (isFailureResult(r.result)) next.add(r.delivery_id);
      }
      return next;
    });
  }, [rows]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const doBulkReplay = useCallback(async () => {
    if (!rows) return;
    const ids = rows
      .filter((r) => selectedIds.has(r.delivery_id))
      .map((r) => r.delivery_id);
    if (ids.length === 0) return;
    if (bulkState && bulkState.done < bulkState.total) return;
    setBulkState({ total: ids.length, done: 0, ok: 0, failures: [] });
    let ok = 0;
    const failures: Array<{ deliveryId: string; reason: string }> = [];
    // Serial: keep the customer's endpoint from taking N simultaneous
    // hits. If bulk grows past ~50 we'd add a small worker-pool here.
    for (const deliveryId of ids) {
      try {
        await replayWebhookDelivery(slug, deliveryId);
        ok++;
      } catch (err) {
        failures.push({
          deliveryId,
          reason: err instanceof Error ? err.message : 'Replay failed.',
        });
      }
      setBulkState((cur) =>
        cur
          ? { ...cur, done: cur.done + 1, ok, failures: [...failures] }
          : cur,
      );
    }
    // Refresh the list so the new delivery rows show up in-place.
    try {
      const res = await listWebhookDeliveries(slug, endpointId, 100);
      setRows(res.deliveries);
    } catch {
      // Non-fatal — the replays already happened server-side; the
      // list just won't refresh. The user can close/reopen the drawer.
    }
    setSelectedIds(new Set());
  }, [rows, selectedIds, slug, endpointId, bulkState]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listWebhookDeliveries(slug, endpointId, 100);
        if (!cancelled) setRows(res.deliveries);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load deliveries.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, endpointId]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!rows || rows.length === 0) return;
    switch (event.key) {
      case 'j':
      case 'ArrowDown': {
        event.preventDefault();
        setFocusIndex((i) => Math.min(i + 1, rows.length - 1));
        break;
      }
      case 'k':
      case 'ArrowUp': {
        event.preventDefault();
        setFocusIndex((i) => Math.max(i - 1, 0));
        break;
      }
      case 'Enter':
      case ' ': {
        event.preventDefault();
        const focused = rows[focusIndex];
        if (focused) {
          setExpandedId((cur) => (cur === focused.delivery_id ? null : focused.delivery_id));
        }
        break;
      }
      case 'c':
      case 'C': {
        // Cmd/Ctrl+C should stay a browser copy — only unmodified 'c'
        // triggers the shortcut, so a user can still copy selected text.
        if (event.metaKey || event.ctrlKey) return;
        const focused = rows[focusIndex];
        if (!focused) return;
        event.preventDefault();
        void navigator.clipboard.writeText(focused.idempotency_key).then(() => {
          setCopiedKey(focused.delivery_id);
          setTimeout(() => setCopiedKey((k) => (k === focused.delivery_id ? null : k)), 1600);
        });
        break;
      }
      case 'r':
      case 'R': {
        // PR-Cluster-7-g: re-dispatch the focused delivery. Cmd/Ctrl+R
        // stays a browser reload; only unmodified 'r' fires.
        if (event.metaKey || event.ctrlKey) return;
        const focused = rows[focusIndex];
        if (!focused) return;
        event.preventDefault();
        void doReplay(focused);
        break;
      }
    }
  };

  if (error) return <Alert variant="error">{error}</Alert>;
  if (!rows) return <Spinner label="Loading deliveries" />;
  if (rows.length === 0)
    return (
      <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
        No delivery attempts recorded in the last 30 days.
      </p>
    );

  const failureCount = rows.filter((r) => isFailureResult(r.result)).length;
  const bulkBusy = bulkState !== null && bulkState.done < bulkState.total;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <BulkReplayBar
        selectedCount={selectedIds.size}
        failureCount={failureCount}
        totalCount={rows.length}
        onSelectFailures={selectAllFailures}
        onClear={clearSelection}
        onReplay={() => void doBulkReplay()}
        state={bulkState}
        busy={bulkBusy}
      />
      <div
        role="listbox"
        aria-label="Recent webhook deliveries"
        aria-activedescendant={rows[focusIndex] ? `dlv-${rows[focusIndex]!.delivery_id}` : undefined}
        tabIndex={0}
        onKeyDown={onKeyDown}
        style={{
          border: '1px solid var(--border)',
          borderRadius: '4px',
          maxHeight: '20rem',
          overflow: 'auto',
          outline: 'none',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 'var(--font-size-3xs)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <thead>
            <tr
              style={{
                background: 'var(--surface-alt)',
                textAlign: 'left',
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontWeight: 600,
              }}
            >
              <th style={{ padding: 'var(--space-1) var(--space-2)', width: '2rem' }} aria-label="select" />
              <th style={{ padding: 'var(--space-1) var(--space-2)' }}>Attempted</th>
              <th style={{ padding: 'var(--space-1) var(--space-2)' }}>Result</th>
              <th style={{ padding: 'var(--space-1) var(--space-2)' }}>Status</th>
              <th style={{ padding: 'var(--space-1) var(--space-2)' }}>Latency</th>
              <th style={{ padding: 'var(--space-1) var(--space-2)' }}>Event</th>
              <th style={{ padding: 'var(--space-1) var(--space-2)' }}>Idempotency-key</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const isFocused = i === focusIndex;
              const isExpanded = expandedId === r.delivery_id;
              return (
                <Fragment key={r.delivery_id}>
                  <tr
                    id={`dlv-${r.delivery_id}`}
                    role="option"
                    aria-selected={isFocused}
                    onClick={() => setFocusIndex(i)}
                    onDoubleClick={() =>
                      setExpandedId((cur) => (cur === r.delivery_id ? null : r.delivery_id))
                    }
                    style={{
                      borderTop: '1px solid var(--border)',
                      background: isFocused ? 'var(--surface-alt)' : 'transparent',
                      outline: isFocused ? '2px solid var(--accent)' : 'none',
                      outlineOffset: '-2px',
                      cursor: 'pointer',
                    }}
                  >
                    <td
                      style={{ padding: 'var(--space-1) var(--space-2)', width: '2rem' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        aria-label={`Select delivery ${r.delivery_id.slice(0, 8)} for bulk replay`}
                        checked={selectedIds.has(r.delivery_id)}
                        onChange={() => toggleSelected(r.delivery_id)}
                        disabled={bulkBusy}
                      />
                    </td>
                    <td style={{ padding: 'var(--space-1) var(--space-2)', color: 'var(--text-muted)' }}>
                      <time dateTime={r.attempted_at}>{fmtDate(r.attempted_at)}</time>
                    </td>
                    <td style={{ padding: 'var(--space-1) var(--space-2)' }}>
                      <LastDeliveryTag result={r.result} />
                    </td>
                    <td style={{ padding: 'var(--space-1) var(--space-2)', color: 'var(--text)' }}>
                      {r.response_status ?? '—'}
                    </td>
                    <td style={{ padding: 'var(--space-1) var(--space-2)', color: 'var(--text-muted)' }}>
                      {r.response_ms == null ? '—' : `${r.response_ms}ms`}
                    </td>
                    <td
                      style={{
                        padding: 'var(--space-1) var(--space-2)',
                        fontFamily: 'var(--font-mono)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      <span title={r.event_id}>{r.event_id.slice(0, 12)}…</span>
                    </td>
                    <td
                      style={{
                        padding: 'var(--space-1) var(--space-2)',
                        fontFamily: 'var(--font-mono)',
                        color: 'var(--text-dim)',
                      }}
                    >
                      <span title={r.idempotency_key}>{r.idempotency_key.slice(0, 8)}…</span>
                      {copiedKey === r.delivery_id ? (
                        <span
                          style={{
                            marginLeft: 'var(--space-2)',
                            color: 'var(--accent)',
                            fontFamily: 'var(--font-ui)',
                          }}
                        >
                          ✓ copied
                        </span>
                      ) : null}
                    </td>
                  </tr>
                  {isExpanded ? (
                    <tr>
                      <td colSpan={7} style={{ padding: 0, background: 'var(--surface-alt)' }}>
                        <DeliveryDetail
                          row={r}
                          replaying={replayingId === r.delivery_id}
                          replayedTo={
                            replayedFlash?.priorId === r.delivery_id
                              ? replayedFlash.newDeliveryId
                              : null
                          }
                          onReplay={() => doReplay(r)}
                        />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text-dim)',
          fontFamily: 'var(--font-mono)',
        }}
      >
        <kbd style={KBD}>j</kbd>/<kbd style={KBD}>k</kbd> move · <kbd style={KBD}>Enter</kbd>{' '}
        expand · <kbd style={KBD}>C</kbd> copy idempotency-key ·{' '}
        <kbd style={KBD}>R</kbd> replay · Focus the list to enable.
      </p>
    </div>
  );
}

const KBD: React.CSSProperties = {
  display: 'inline-block',
  padding: '0 4px',
  border: '1px solid var(--border-strong)',
  borderRadius: '3px',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: 'var(--font-size-3xs)',
  fontFamily: 'var(--font-mono)',
  lineHeight: 1.4,
};

function DeliveryDetail({
  row,
  replaying,
  replayedTo,
  onReplay,
}: {
  row: WebhookDeliveryRow;
  replaying: boolean;
  replayedTo: string | null;
  onReplay: () => void;
}) {
  return (
    <div
      style={{
        borderTop: '1px solid var(--border-strong)',
        padding: 'var(--space-3) var(--space-4)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)', alignItems: 'center' }}>
        {replayedTo ? (
          <span
            style={{
              fontSize: 'var(--font-size-3xs)',
              color: 'var(--accent)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            ✓ replayed → {replayedTo.slice(0, 12)}…
          </span>
        ) : null}
        <Button
          variant="secondary"
          compact
          onClick={onReplay}
          disabled={replaying}
          title="Re-dispatch this event to the endpoint. attempt_number increments and is_replay=1 on the new delivery row."
        >
          {replaying ? 'Replaying…' : 'Replay'}
        </Button>
      </div>
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(9rem, auto) 1fr',
          gap: '4px var(--space-3)',
          margin: 0,
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text-muted)',
        }}
      >
      <DetailRow label="Delivery ID">
        <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>
          {row.delivery_id}
        </code>
      </DetailRow>
      <DetailRow label="Event ID">
        <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>
          {row.event_id}
        </code>
      </DetailRow>
      <DetailRow label="Idempotency key">
        <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>
          {row.idempotency_key}
        </code>
      </DetailRow>
      <DetailRow label="Attempted">
        <time dateTime={row.attempted_at}>{row.attempted_at}</time>
      </DetailRow>
      <DetailRow label="Finished">
        {row.finished_at ? (
          <time dateTime={row.finished_at}>{row.finished_at}</time>
        ) : (
          <span style={{ color: 'var(--text-dim)' }}>in-flight or lost</span>
        )}
      </DetailRow>
      <DetailRow label="Request timestamp">
        <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>
          {row.request_timestamp}
        </code>
      </DetailRow>
      <DetailRow label="Signature">
        <code
          style={{
            fontFamily: 'var(--font-mono)',
            color: 'var(--text)',
            wordBreak: 'break-all',
          }}
        >
          {row.request_signature}
        </code>
      </DetailRow>
      <DetailRow label="Attempt">
        {row.attempt_number}
        {row.is_replay ? (
          <span
            style={{
              marginLeft: 'var(--space-2)',
              padding: '0 6px',
              borderRadius: '999px',
              border: '1px solid var(--band-amber)',
              background: 'var(--warning-bg)',
              color: 'var(--warning-fg)',
              fontSize: 'var(--font-size-3xs)',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            Replay
          </span>
        ) : null}
      </DetailRow>
    </dl>
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt
        style={{
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text-dim)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
        }}
      >
        {label}
      </dt>
      <dd style={{ margin: 0, color: 'var(--text)' }}>{children}</dd>
    </>
  );
}

// Badge import kept for tree-shaking symmetry with sibling settings pages
// that reach for it — signals to future contributors that DS Badge is the
// canonical replacement when this page eventually grows chip UI.
void Badge;

// ---- Bulk replay (PR-Cluster-7-i) -----------------------------------

function isFailureResult(result: WebhookDeliveryResult): boolean {
  return result === 'failure_4xx' || result === 'failure_5xx' || result === 'failure_timeout' || result === 'failure_dns';
}

function BulkReplayBar(props: {
  selectedCount: number;
  failureCount: number;
  totalCount: number;
  onSelectFailures: () => void;
  onClear: () => void;
  onReplay: () => void;
  state: {
    total: number;
    done: number;
    ok: number;
    failures: Array<{ deliveryId: string; reason: string }>;
  } | null;
  busy: boolean;
}) {
  const complete = props.state !== null && props.state.done >= props.state.total;
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-3)',
        alignItems: 'center',
        flexWrap: 'wrap',
        padding: 'var(--space-2) var(--space-3)',
        border: '1px solid var(--border)',
        borderRadius: '4px',
        background: 'var(--surface-alt)',
        fontSize: 'var(--font-size-3xs)',
        color: 'var(--text-muted)',
      }}
    >
      <span>
        <strong style={{ color: 'var(--text)', fontWeight: 700 }}>{props.selectedCount}</strong>{' '}
        selected {props.selectedCount > 0 ? `of ${props.totalCount}` : ''}
      </span>
      <Button
        variant="secondary"
        compact
        onClick={props.onSelectFailures}
        disabled={props.busy || props.failureCount === 0}
        title={
          props.failureCount === 0
            ? 'No failure deliveries in the current window.'
            : `Add every failure_4xx / 5xx / timeout / dns row (${props.failureCount}) to the selection.`
        }
      >
        Select failures ({props.failureCount})
      </Button>
      <Button
        variant="secondary"
        compact
        onClick={props.onClear}
        disabled={props.busy || props.selectedCount === 0}
      >
        Clear
      </Button>
      <Button
        variant="primary"
        compact
        onClick={props.onReplay}
        disabled={props.busy || props.selectedCount === 0}
        title="Re-dispatch every selected delivery to its endpoint, one at a time. Each fires a webhook.replayed audit event."
      >
        {props.busy && props.state
          ? `Replaying ${props.state.done}/${props.state.total}…`
          : `Replay ${props.selectedCount} selected`}
      </Button>
      {complete && props.state ? (
        <span
          style={{
            color: props.state.failures.length === 0 ? 'var(--success-fg)' : 'var(--warning-fg)',
            fontWeight: 600,
          }}
          role="status"
        >
          {props.state.failures.length === 0
            ? `✓ Replayed ${props.state.ok} of ${props.state.total}.`
            : `Replayed ${props.state.ok} of ${props.state.total} · ${props.state.failures.length} failed.`}
        </span>
      ) : null}
      {complete && props.state && props.state.failures.length > 0 ? (
        <details style={{ width: '100%', marginTop: 'var(--space-1)' }}>
          <summary style={{ cursor: 'pointer', color: 'var(--warning-fg)' }}>
            {props.state.failures.length} replay failure{props.state.failures.length === 1 ? '' : 's'}
          </summary>
          <ul
            style={{
              margin: 'var(--space-2) 0 0',
              paddingLeft: '1.25rem',
              fontFamily: 'var(--font-mono)',
              color: 'var(--text-muted)',
            }}
          >
            {props.state.failures.map((f) => (
              <li key={f.deliveryId}>
                <code style={{ color: 'var(--text)' }}>{f.deliveryId.slice(0, 12)}…</code> —{' '}
                {f.reason}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
